import { useEffect, useRef, useState } from 'react';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import { buildPostedHistory } from './historyMock';
import { postingFromHistoryRow } from './fixtures';
import { useNavigate, useParams, Link } from 'react-router-dom';
import './posting.css';
import type { Tab } from './types';
import {
  answerItem,
  bankDiffCountSync,
  changeAnswer,
  confirmReturn,
  dismissMilestone,
  getAutopilotRowsSync,
  getConfirmedSync,
  getDifferenceSync,
  getDifferencesSync,
  getItemSync,
  getModeSync,
  getPostingSync,
  groupedToPost,
  isDevMode,
  getCheckSync,
  getUploadsSync,
  matchedMonthCountSync,
  checksForGroup,
  pickClaim,
  postItMyself,
  searchClaims,
  setMode,
  setReturnChoice,
  shouldShowMilestone,
  simulateNewEra,
  toPostCountSync,
  toPostTotalSync,
  postedMonthCountSync,
  touchOpen,
  usePostingVersion,
} from './store';
import { useAppealsWaitingCount } from '@/features/appeals/store';
import { useNetworksVersion } from '@/features/networks/store';
import { usePaidToPatientVersion } from '@/features/networks/paidToPatient';
import { Header } from './components/Header';
import { ToPostList } from './components/ToPostList';
import { Detail } from './components/Detail';
import { PostedDetail } from './components/Posted';
import { PostedHistory } from './components/PostedHistory';
import { BankDetail, BankList } from './components/Bank';
import { AutopilotSummary } from './components/Autopilot';
import { Milestone } from './components/Milestone';
import { MatchedView } from './components/Matched';
import { useStatementUpload, type ToastAction } from './components/StatementUpload';
import { CheckDetail } from './components/CheckDetail';
import { PageViewer } from './components/EobDialogs';
import { PMS } from '@/features/shared/pms';

interface Toast { id: number; msg: string; action?: ToastAction }

function useIsDesktop(): boolean {
  const [d, setD] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true));
  useEffect(() => {
    const m = window.matchMedia('(min-width: 1024px)');
    const h = () => { setD(m.matches); };
    m.addEventListener('change', h);
    return () => { m.removeEventListener('change', h); };
  }, []);
  return d;
}

export function Posting({ view }: { view: Tab }) {
  usePostingVersion();
  useNetworksVersion();
  usePaidToPatientVersion();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { id } = useParams();

  const mode = getModeSync();
  const deniedCount = useAppealsWaitingCount();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pageViewer, setPageViewer] = useState<{ pages: number; start: number } | null>(null);
  const toastSeq = useRef(0);
  const dev = isDevMode();

  useEffect(() => { if (id) touchOpen(id); }, [id]);

  const { needsYou, ready, checking } = groupedToPost();
  const allToPost = [...needsYou, ...ready, ...checking];

  // auto-open first item on a bare tab (desktop)
  useEffect(() => {
    if (id || !isDesktop) return;
    if (view === 'to_post' && mode === 'training' && allToPost.length > 0) {
      const first = allToPost[0];
      if (first) void navigate(`/posting/${first.id}`, { replace: true });
    }
    if (view === 'bank') {
      const first = getDifferencesSync()[0];
      if (first) void navigate(`/posting/bank/${first.id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isDesktop, view, mode, allToPost.length]);

  const addToast = (msg: string, action?: ToastAction, ms = 6000): void => {
    const tid = ++toastSeq.current;
    setToasts((t) => [...t, { id: tid, msg, ...(action ? { action } : {}) }]);
    setTimeout(() => { setToasts((t) => t.filter((x) => x.id !== tid)); }, ms);
  };
  const upload = useStatementUpload(addToast);

  const tabTo = (t: Tab): string =>
    t === 'bank' ? '/posting/bank' : t === 'posted' ? '/posting/posted' : t === 'matched' ? '/posting/matched' : '/posting';

  const header = (
    <>
    <Header
      view={view}
      mode={mode}
      confirmed={getConfirmedSync()}
      toPostCount={toPostCountSync()}
      toPostTotal={toPostTotalSync()}
      needsCount={needsYou.length + checksForGroup('needs_you').length}
      postedCount={postedMonthCountSync()}
      bankCount={bankDiffCountSync()}
      matchedCount={matchedMonthCountSync()}
      uploading={upload.uploading}
      onUpload={upload.open}
      tabTo={tabTo}
    />
    {upload.uploading ? <div className="po-readbar" role="progressbar" aria-label="Reading the bank statement"><i /></div> : null}
    </>
  );

  const devButton = dev ? (
    <div className="po-dev"><button type="button" className="po-btn po-btn-primary po-btn-sm" onClick={() => { simulateNewEra(); addToast('Added a new ERA.'); }}>Simulate a new ERA</button></div>
  ) : null;

  const toastEls = (
    <>
      {upload.node}
      {toasts.length > 0 ? (
        <div className="po-toasts">
          {toasts.map((t) => (
            <div key={t.id} className="po-toast" role="status">
              <span>{t.msg}</span>
              {t.action ? (
                <button type="button" className="po-toast-undo" onClick={() => { t.action?.onClick(); setToasts((all) => all.filter((x) => x.id !== t.id)); }}>
                  {t.action.label}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </>
  );

  const milestoneEl = shouldShowMilestone() ? (
    <Milestone onTurnOn={() => { setMode('autopilot'); addToast('Autopilot is on.'); void navigate('/posting'); }} onKeep={() => { dismissMilestone(); }} />
  ) : null;

  const eobDialogs = pageViewer ? <PageViewer pages={pageViewer.pages} start={pageViewer.start} onClose={() => { setPageViewer(null); }} /> : null;

  // ---- Posted tab (history at scale) ----
  if (view === 'posted') {
    // A direct link to a specific posting still opens its full detail.
    if (id) {
      // A live posting, or a row of the Posted history (every row is routable, D202).
      const histRow = getPostingSync(id) ? null : buildPostedHistory().find((r) => r.id === id);
      const posting = getPostingSync(id) ?? (histRow ? postingFromHistoryRow(histRow) : null);
      return (
        <div className="po po-page">
          {header}
          <div className="po-body">
            {posting ? (
              <PostedDetail p={posting} onBack={() => { void navigate('/posting/posted'); }} />
            ) : (
              <NotFoundInPage as="main" thing="payment" id={id} listLabel="posted payments" listTo="/posting/posted" />
            )}
          </div>
          {devButton}
          {toastEls}
          {milestoneEl}
        </div>
      );
    }
    return (
      <div className="po po-page">
        {header}
        <PostedHistory />
        {devButton}
        {toastEls}
        {milestoneEl}
      </div>
    );
  }

  // ---- Matched tab (rcm-32) ----
  if (view === 'matched') {
    const mobileClass = !isDesktop ? (id ? 'po-two-detail' : 'po-two-queue') : '';
    return (
      <div className={`po po-page ${mobileClass}`}>
        {header}
        <MatchedView {...(id ? { id } : {})} isDesktop={isDesktop} addToast={addToast} />
        {devButton}
        {toastEls}
        {milestoneEl}
      </div>
    );
  }

  // ---- Bank tab ----
  if (view === 'bank') {
    const diffs = getDifferencesSync();
    const noStatement = getUploadsSync().length === 0;
    const selected = id ? getDifferenceSync(id) : null;
    const mobileClass = !isDesktop ? (id ? 'po-two-detail' : 'po-two-queue') : '';
    let detail: React.ReactNode = null;
    if (noStatement) {
      detail = <main className="po-detail"><div className="po-empty"><p className="po-empty-title">No statement yet.</p><p className="po-meta">Use Upload bank statement at the top right to compare deposits with what was posted.</p></div></main>;
    } else if (selected && !selected.removed) {
      detail = <BankDetail d={selected} onLeave={() => { void navigate('/posting/bank'); }} onOpenToPost={() => { void navigate('/posting'); }} {...(!isDesktop ? { onBack: () => { void navigate('/posting/bank'); } } : {})} />;
    } else if (id) {
      detail = <NotFoundInPage as="main" thing="deposit" id={id} listLabel="bank deposits" listTo="/posting/bank" />;
    }
    return (
      <div className={`po po-page ${mobileClass}`}>
        {header}
        <div className="po-body">
          <BankList differences={diffs} {...(id ? { selectedId: id } : {})} onSelect={(bid) => { void navigate(`/posting/bank/${bid}`); }} />
          {detail}
        </div>
        {devButton}
        {toastEls}
        {milestoneEl}
      </div>
    );
  }

  // ---- To post tab ----
  // autopilot: summary in the detail area when nothing selected
  if (mode === 'autopilot' && !id) {
    return (
      <div className="po po-page">
        {header}
        <div className="po-body">
          <ToPostList needsYou={needsYou} ready={ready} checking={checking} deniedCount={deniedCount} onSelect={(iid) => { void navigate(`/posting/${iid}`); }} />
          <AutopilotSummary rows={getAutopilotRowsSync()} onReview={(rid) => { void navigate(`/posting/${rid}`); }} onSeePosted={() => { void navigate('/posting/posted'); }} />
        </div>
        {devButton}
        {toastEls}
        {milestoneEl}
      </div>
    );
  }

  // all clear (training, empty)
  if (allToPost.length === 0 && !id) {
    return (
      <div className="po po-page">
        {header}
        <div className="po-wide">
          <section className="po-card">
            <h2 className="po-h2">Nothing to post</h2>
            <p style={{ color: 'var(--fg2)' }}>
              New insurance payments appear here as soon as they reach {PMS}. ELVA posted {postedMonthCountSync()} this month, each one confirmed by your team.{' '}
              <button type="button" className="po-src" style={{ textDecoration: 'underline' }} onClick={() => { void navigate('/posting/posted'); }}>See them in Posted</button>
            </p>
            <p className="po-meta" style={{ marginTop: 10 }}>Posted, but paid $0 or less than your contract: <Link to="/denied" className="po-src" style={{ textDecoration: 'underline' }}>{deniedCount} claims on Denied &amp; underpaid</Link></p>
          </section>
        </div>
        {devButton}
        {toastEls}
        {milestoneEl}
      </div>
    );
  }

  const selected = id ? getItemSync(id) : null;
  const selectedCheck = id ? getCheckSync(id) : null;
  const mobileClass = !isDesktop ? (id ? 'po-two-detail' : 'po-two-queue') : '';

  const nextAfter = (curId: string): void => {
    const g = groupedToPost();
    const rest = [...g.needsYou, ...g.ready, ...g.checking].filter((t) => t.id !== curId);
    const first = rest[0];
    if (first) void navigate(`/posting/${first.id}`);
    else void navigate('/posting');
  };

  let detail: React.ReactNode = null;
  if (selectedCheck && !selectedCheck.allPosted) {
    detail = (
      <CheckDetail
        key={selectedCheck.id}
        check={selectedCheck}
        onOpenPage={(page) => { setPageViewer({ pages: 4, start: page }); }}
        onSeePosted={() => { void navigate('/posting/posted'); }}
        {...(!isDesktop ? { onBack: () => { void navigate('/posting'); } } : {})}
      />
    );
  } else if (selected) {
    detail = (
      <Detail
        key={selected.id}
        item={selected}
        onAnswer={(choice) => {
          const opt = selected.question?.options.find((o) => o.id === choice);
          answerItem(selected.id, choice);
          if (opt?.effect === 'appeal') { addToast(`${selected.firstName} moved to Denied & underpaid.`); nextAfter(selected.id); }
          else if (opt?.effect === 'dispute') { addToast(`Disputed. ELVA prepares the letter on Denied & underpaid.`); nextAfter(selected.id); }
          else if (opt?.effect === 'pms_there') { addToast(`Already posted in ${PMS}. ELVA linked it and didn’t post again.`); nextAfter(selected.id); }
          else if (opt?.effect === 'network_in' || opt?.effect === 'network_out') { addToast(`Logged in Settings › Practice details › Networks. ${selected.firstName}’s posting can go ahead.`); }
        }}
        onPickClaim={(cid) => { pickClaim(selected.id, cid); }}
        onSearch={(q) => searchClaims(selected.id, q)}
        onSomethingWrong={(note) => { const w = selected.question?.options.find((o) => o.effect === 'something_wrong'); if (w) answerItem(selected.id, w.id, { note }); addToast('Sent to ELVA. This stays open.'); }}
        onReturnChoice={(c) => { setReturnChoice(selected.id, c); }}
        onConfirmReturn={() => { confirmReturn(selected.id); addToast(`${selected.firstName}’s payment is being returned.`); nextAfter(selected.id); }}
        onPostMyself={() => { postItMyself(selected.id); addToast(`${selected.firstName} removed. You’ll post it in ${PMS}.`); nextAfter(selected.id); }}
        onChangeAnswer={() => { changeAnswer(selected.id); }}
        onSeeSteps={(pid) => { void navigate(`/posting/posted/${pid}`); }}
        {...(!isDesktop ? { onBack: () => { void navigate('/posting'); } } : {})}
      />
    );
  } else if (id) {
    detail = <NotFoundInPage as="main" thing="payment" id={id} listLabel="payments to post" listTo="/posting" />;
  }

  return (
    <div className={`po po-page ${mobileClass}`}>
      {header}
      <div className="po-body">
        <ToPostList
          needsYou={needsYou}
          ready={ready}
          checking={checking}
          needsYouChecks={checksForGroup('needs_you')}
          readyChecks={checksForGroup('ready')}
          {...(id ? { selectedId: id } : {})}
          deniedCount={deniedCount}
          onSelect={(iid) => { void navigate(`/posting/${iid}`); }}
        />
        {detail}
      </div>
      {devButton}
      {toastEls}
      {milestoneEl}
      {eobDialogs}
    </div>
  );
}
